import {
  useGetMentorQualityScores,
  useGetQualityScoringRuns,
  useGetSessionQualityScores,
  useGetSessionQualityScoresExport,
} from '@actions';
import Button from '@components/V4/Button';
import QualityScoreBadge from '@components/V4/QualityScore/QualityScoreBadge';
import QualityScoreDetails, {
  DIMENSION_KEYS,
  DIMENSION_SHORT_LABELS,
  formatScore,
  LOW_SCORE_THRESHOLD,
  scoreTone,
} from '@components/V4/QualityScore/QualityScoreDetails';
import { ChevronDownIcon, ClipboardCopyIcon, StarIcon } from '@heroicons/react/outline';
import DetailsModal from '@modules/Admin/components/DetailsModal/DetailsModal';
import useDetailsModal from '@modules/Admin/hooks/useDetailsModal';
import {
  QualityScoringRunDTO,
  SessionQualityScoreExportRowDTO,
  SessionQualityScoreRowDTO,
} from '@shared/clientTypes/DTOs/AdminMetricsDTO';
import { SessionQualityScoreDimension } from '@shared/common';
import { Checkbox, Spin } from 'antd';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { stringify } from 'csv-stringify/sync';
import moment from 'moment';
import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { ColumnHeader, EmptyState, MetricCard, SectionHeading, TableShell } from './common';

const shortId = (id: string) => `…${id.slice(-6)}`;

const downloadFile = (content: string, mimeType: string, fileName: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const transcriptToText = (row: SessionQualityScoreExportRowDTO) =>
  (row.meeting?.newTranscript?.content ?? [])
    .map((entry) => `[${entry.startTime}] ${entry.user}: ${entry.text}`)
    .join('\n');

const sessionsToCsv = (rows: SessionQualityScoreExportRowDTO[]) => {
  const header = [
    'meetingDate',
    'mentor',
    'student',
    'course',
    'session',
    'sessionStatus',
    'score',
    ...DIMENSION_KEYS.map((key) => `dim${key}_${DIMENSION_SHORT_LABELS[key]}`),
    'status',
    'gradedAt',
    'graderModel',
    'meetingId',
    'sessionId',
    'enrollmentId',
    'feedback',
    'newTranscript',
  ];
  const body = rows.map((row) => [
    row.meeting?.scheduledDate ?? '',
    row.mentor?.fullName ?? '',
    row.student.fullName,
    row.course.abbreviation || row.course.title,
    row.sessionName || `Session ${row.sessionIndex + 1}`,
    row.sessionStatus ?? '',
    row.qualityScoring.score ?? '',
    ...DIMENSION_KEYS.map((key) => {
      const dim = row.qualityScoring.dims?.[key];
      return dim?.na ? 'n/a' : dim?.score ?? '';
    }),
    row.qualityScoring.status ?? '',
    row.qualityScoring.graded_at ?? '',
    row.qualityScoring.grader?.model ?? '',
    row.qualityScoring.meeting_id,
    row.sessionId,
    row.enrollmentId,
    row.qualityScoring.feedback?.text ?? '',
    transcriptToText(row),
  ]);
  return stringify([header, ...body]);
};

const runsToCsv = (runs: QualityScoringRunDTO[]) => {
  const header = [
    'runId',
    'night',
    'meetingId',
    'status',
    'pos',
    'startedAt',
    'finishedAt',
    'batchPass1',
    'batchPass2',
    'usageUsd',
    'score',
  ];
  const body = runs.map((run) => [
    run._id,
    run.night ?? '',
    run.meeting_id ?? '',
    run.status ?? '',
    run.pos ?? '',
    run.started_at ?? '',
    run.finished_at ?? '',
    run.batch_ids?.pass1 ?? '',
    run.batch_ids?.pass2 ?? '',
    run.usage_usd ?? '',
    run.score?.score ?? '',
  ]);
  return stringify([header, ...body]);
};

const CopyableId = (props: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);

  const copyHandler = () => {
    navigator.clipboard?.writeText(props.value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      type="button"
      onClick={copyHandler}
      title={`${props.label}: ${props.value} (click to copy)`}
      className="inline-flex items-center gap-1 rounded-md bg-surfaceHover px-2 py-0.5 font-mono text-xs text-customLight transition-colors hover:bg-white/10"
    >
      <span className="text-customGrey">{props.label}</span>
      <span>{shortId(props.value)}</span>
      <ClipboardCopyIcon className="h-3.5 w-3.5 text-customGrey" />
      {copied ? <span className="text-customGreen">copied</span> : null}
    </button>
  );
};

const RowDetails = (props: { row: SessionQualityScoreRowDTO }) => {
  const { row } = props;

  return (
    <QualityScoreDetails
      className="rounded-2xl bg-surfaceBackground p-5"
      qualityScoring={row.qualityScoring}
      headerSlot={
        <>
          <CopyableId label="meeting" value={row.qualityScoring.meeting_id} />
          <CopyableId label="session" value={row.sessionId} />
          <CopyableId label="enrollment" value={row.enrollmentId} />
        </>
      }
    />
  );
};

const getRowKey = (row: { enrollmentId: string; sessionId: string }) =>
  `${row.enrollmentId}-${row.sessionId}`;

const LinkButton = (props: { children: ReactNode; onClick: () => void }) => (
  <button
    type="button"
    onClick={props.onClick}
    className="text-left text-customLight underline-offset-2 hover:text-customPrimary hover:underline"
  >
    {props.children}
  </button>
);

const QualityScoresTab = (props: { month: string }) => {
  const { data: rows, isLoading } = useGetSessionQualityScores({ month: props.month });
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [props.month]);

  const allRowKeys = useMemo(() => (rows ?? []).map(getRowKey), [rows]);
  const selectedRowKeySet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);
  const selectedCount = selectedRowKeys.length;
  const areAllSelected = allRowKeys.length > 0 && selectedCount === allRowKeys.length;

  const toggleRow = (rowKey: string) =>
    setSelectedRowKeys((prev) =>
      prev.includes(rowKey) ? prev.filter((key) => key !== rowKey) : [...prev, rowKey]
    );

  const toggleAll = () => setSelectedRowKeys(areAllSelected ? [] : allRowKeys);

  const exportSessionsQuery = useGetSessionQualityScoresExport(
    { month: props.month },
    { enabled: false }
  );
  const exportRunsQuery = useGetQualityScoringRuns({ month: props.month }, { enabled: false });

  const exportSessionsHandler = async (format: 'csv' | 'json') => {
    const { data: allData } = await exportSessionsQuery.refetch();
    const data = allData?.filter((row) => selectedRowKeySet.has(getRowKey(row)));
    if (!data?.length) return;

    if (format === 'csv') {
      downloadFile(
        sessionsToCsv(data),
        'text/csv;charset=utf-8;',
        `quality-scores-${props.month}.csv`
      );
    } else {
      downloadFile(
        JSON.stringify(data, null, 2),
        'application/json',
        `quality-scores-${props.month}.json`
      );
    }
  };

  const exportRunsHandler = async (format: 'csv' | 'json') => {
    const selectedMeetingIds = new Set(
      (rows ?? [])
        .filter((row) => selectedRowKeySet.has(getRowKey(row)))
        .map((row) => row.qualityScoring.meeting_id)
    );
    const { data: allData } = await exportRunsQuery.refetch();
    const data = allData?.filter((run) => run.meeting_id && selectedMeetingIds.has(run.meeting_id));
    if (!data?.length) return;

    if (format === 'csv') {
      downloadFile(
        runsToCsv(data),
        'text/csv;charset=utf-8;',
        `quality-scoring-runs-${props.month}.csv`
      );
    } else {
      downloadFile(
        JSON.stringify(data, null, 2),
        'application/json',
        `quality-scoring-runs-${props.month}.json`
      );
    }
  };

  const {
    activeModalView,
    isModalOpen,
    mentorId,
    studentId,
    sessionId,
    enrollmentId,
    openModal,
    closeModal,
  } = useDetailsModal({ page: 'session' });

  const { data: mentorQualityScores } = useGetMentorQualityScores();
  const mentorSummaries = mentorQualityScores ?? [];

  const monthAverageByMentor = useMemo(() => {
    const grouped = new Map<string, number[]>();
    (rows ?? []).forEach((row) => {
      if (!row.mentor?._id || typeof row.qualityScoring.score !== 'number') return;
      grouped.set(row.mentor._id, [
        ...(grouped.get(row.mentor._id) ?? []),
        row.qualityScoring.score,
      ]);
    });
    return new Map(
      [...grouped.entries()].map(([mentorId, scores]) => [
        mentorId,
        { average: scores.reduce((sum, s) => sum + s, 0) / scores.length, count: scores.length },
      ])
    );
  }, [rows]);

  const summary = useMemo(() => {
    const list = rows ?? [];
    const scores = list.map((row) => row.qualityScoring.score).filter((s) => typeof s === 'number');
    const average = scores.length ? scores.reduce((sum, s) => sum + s, 0) / scores.length : null;
    const belowThreshold = scores.filter((s) => s < LOW_SCORE_THRESHOLD).length;

    const dimensionAverages = DIMENSION_KEYS.map((key) => {
      const values = list
        .map((row) => row.qualityScoring.dims?.[key])
        .filter((dim): dim is SessionQualityScoreDimension => !!dim && !dim.na)
        .map((dim) => dim.score)
        .filter((s): s is number => typeof s === 'number');
      return {
        key,
        name: list.find((row) => row.qualityScoring.dims?.[key]?.name)?.qualityScoring.dims[key]
          .name,
        average: values.length ? values.reduce((sum, s) => sum + s, 0) / values.length : null,
        sampleSize: values.length,
      };
    });

    return { graded: list.length, average, belowThreshold, dimensionAverages };
  }, [rows]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <section className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
        <MetricCard
          label="Graded sessions"
          value={summary.graded}
          icon={<StarIcon className="h-5 w-5" />}
          accent="teal"
        />
        <MetricCard
          label="Average quality score"
          value={<span className={scoreTone(summary.average)}>{formatScore(summary.average)}</span>}
          icon={<StarIcon className="h-5 w-5" />}
          accent="gold"
        />
        <MetricCard
          label={`Sessions scored below ${LOW_SCORE_THRESHOLD}`}
          value={summary.belowThreshold}
          icon={<StarIcon className="h-5 w-5" />}
          accent="lila"
        />
      </section>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Average by dimension"
          description="Mean dimension score across graded sessions this month; n/a dimensions are excluded."
        />
        <div className="grid grid-cols-2 gap-3 tablet:grid-cols-3 laptop:grid-cols-6">
          {summary.dimensionAverages.map((dim) => (
            <div key={dim.key} className="flex flex-col gap-1 rounded-2xl bg-surfaceObject p-4">
              <span className="text-xs text-customGrey">
                {dim.key}. {dim.name ?? DIMENSION_SHORT_LABELS[dim.key]}
              </span>
              <span
                className={mergeClassNames(
                  'text-2xl font-bold leading-none tabular-nums',
                  scoreTone(dim.average)
                )}
              >
                {formatScore(dim.average)}
              </span>
              <span className="text-xs text-customGrey">
                {dim.sampleSize} {dim.sampleSize === 1 ? 'session' : 'sessions'}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Mentor averages"
          description="Average AI score per mentor across all their graded sessions (all time), worst first. The month column only counts sessions graded in the selected month."
        />
        {mentorSummaries.length > 0 ? (
          <TableShell minWidth="min-w-[900px]">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-customGrey">
                <th className="px-5 py-3 text-left font-semibold">Mentor</th>
                <th className="px-5 py-3 text-left font-semibold">Average (all time)</th>
                <th className="px-5 py-3 text-left font-semibold">This month</th>
                <ColumnHeader>Graded</ColumnHeader>
                <ColumnHeader dot="teal">Good</ColumnHeader>
                <ColumnHeader dot="gold">OK</ColumnHeader>
                <ColumnHeader dot="lila">Poor</ColumnHeader>
                <th className="px-5 py-3 text-left font-semibold">Last graded</th>
              </tr>
            </thead>
            <tbody>
              {mentorSummaries.map((mentor) => {
                const month = monthAverageByMentor.get(mentor.mentorId);

                return (
                  <tr
                    key={mentor.mentorId}
                    className="border-b border-white/5 transition-colors hover:bg-surfaceHover"
                  >
                    <td className="px-5 py-3 text-left">
                      <LinkButton
                        onClick={() =>
                          openModal(
                            { page: 'mentor', tab: 'ai-scoring' },
                            { mentorId: mentor.mentorId }
                          )
                        }
                      >
                        {mentor.mentorName}
                      </LinkButton>
                    </td>
                    <td className="px-5 py-3 text-left">
                      <QualityScoreBadge score={mentor.average} />
                    </td>
                    <td className="px-5 py-3 text-left">
                      <QualityScoreBadge
                        score={month?.average}
                        suffix={
                          month ? (
                            <span className="font-normal opacity-70">({month.count})</span>
                          ) : null
                        }
                      />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-customLight">
                      {mentor.graded}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-customGreen">
                      {mentor.good}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-customGold">
                      {mentor.ok}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-customRed">
                      {mentor.poor}
                    </td>
                    <td className="px-5 py-3 text-left tabular-nums text-customGrey">
                      {mentor.lastGradedAt ? moment(mentor.lastGradedAt).format('D MMM YYYY') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableShell>
        ) : (
          <EmptyState>No mentor has graded sessions yet.</EmptyState>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <SectionHeading
            title="Graded sessions"
            description="Every session with a mentor quality score, referenced to the meeting it was graded from. Expand a row for the per-dimension moments and session feedback."
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-2 text-xs text-customGrey">
              {selectedCount
                ? `${selectedCount} of ${allRowKeys.length} selected`
                : 'Select sessions to export'}
            </span>
            <Button
              variant="secondary"
              buttonText={`Sessions CSV${selectedCount ? ` (${selectedCount})` : ''}`}
              disabled={!selectedCount}
              isLoading={exportSessionsQuery.isFetching}
              onClick={() => exportSessionsHandler('csv')}
            />
            <Button
              variant="secondary"
              buttonText={`Sessions JSON${selectedCount ? ` (${selectedCount})` : ''}`}
              disabled={!selectedCount}
              isLoading={exportSessionsQuery.isFetching}
              onClick={() => exportSessionsHandler('json')}
            />
            <Button
              variant="secondary"
              buttonText="Scoring runs CSV"
              disabled={!selectedCount}
              isLoading={exportRunsQuery.isFetching}
              onClick={() => exportRunsHandler('csv')}
            />
            <Button
              variant="secondary"
              buttonText="Scoring runs JSON"
              disabled={!selectedCount}
              isLoading={exportRunsQuery.isFetching}
              onClick={() => exportRunsHandler('json')}
            />
          </div>
        </div>
        {rows && rows.length > 0 ? (
          <TableShell minWidth="min-w-[1180px]">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-customGrey">
                <th className="px-3 py-3">
                  <Checkbox
                    aria-label="Select all sessions"
                    checked={areAllSelected}
                    indeterminate={selectedCount > 0 && !areAllSelected}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-5 py-3 text-left font-semibold">Meeting date</th>
                <th className="px-5 py-3 text-left font-semibold">Mentor</th>
                <th className="px-5 py-3 text-left font-semibold">Student</th>
                <th className="px-5 py-3 text-left font-semibold">Course</th>
                <th className="px-5 py-3 text-left font-semibold">Session</th>
                <ColumnHeader dot="gold">Score</ColumnHeader>
                {DIMENSION_KEYS.map((key) => (
                  <ColumnHeader key={key}>
                    <span title={rows[0]?.qualityScoring.dims?.[key]?.name}>
                      {DIMENSION_SHORT_LABELS[key]}
                    </span>
                  </ColumnHeader>
                ))}
                <th className="px-5 py-3 text-left font-semibold">Refs</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowKey = getRowKey(row);
                const isExpanded = expandedRowKey === rowKey;
                const isSelected = selectedRowKeySet.has(rowKey);
                const meetingDate = row.meeting?.scheduledDate ?? row.qualityScoring.graded_at;

                return (
                  <Fragment key={rowKey}>
                    <tr
                      className={mergeClassNames(
                        'border-b border-white/5 align-top transition-colors hover:bg-surfaceHover',
                        isExpanded || isSelected ? 'bg-surfaceHover' : ''
                      )}
                    >
                      <td className="px-3 py-3">
                        <Checkbox
                          aria-label="Select session"
                          checked={isSelected}
                          onChange={() => toggleRow(rowKey)}
                        />
                      </td>
                      <td className="px-5 py-3 text-left tabular-nums text-customLight">
                        <div className="flex flex-col">
                          <span>{moment(meetingDate).format('D MMM YYYY')}</span>
                          <span className="text-xs text-customGrey">
                            {moment(meetingDate).format('HH:mm')}
                            {row.meeting ? '' : ' · meeting not found'}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-left">
                        {row.mentor ? (
                          <LinkButton
                            onClick={() =>
                              openModal({ page: 'mentor' }, { mentorId: row.mentor?._id })
                            }
                          >
                            {row.mentor.fullName}
                          </LinkButton>
                        ) : (
                          <span className="text-customGrey">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-left">
                        <LinkButton
                          onClick={() =>
                            openModal({ page: 'student' }, { studentId: row.student._id })
                          }
                        >
                          {row.student.fullName}
                        </LinkButton>
                      </td>
                      <td className="px-5 py-3 text-left">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: row.course.color }}
                          />
                          <span className="text-customLight">
                            {row.course.abbreviation || row.course.title}
                          </span>
                        </span>
                      </td>
                      <td className="px-5 py-3 text-left">
                        <LinkButton
                          onClick={() =>
                            openModal(
                              { page: 'session', tab: 'meetings' },
                              { sessionId: row.sessionId, enrollmentId: row.enrollmentId }
                            )
                          }
                        >
                          {row.sessionName || `Session ${row.sessionIndex + 1}`}
                        </LinkButton>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <QualityScoreBadge score={row.qualityScoring.score} />
                      </td>
                      {DIMENSION_KEYS.map((key) => {
                        const dim = row.qualityScoring.dims?.[key];
                        return (
                          <td
                            key={key}
                            title={dim?.name}
                            className={mergeClassNames(
                              'px-5 py-3 text-right tabular-nums',
                              dim?.na ? 'text-customGrey' : scoreTone(dim?.score)
                            )}
                          >
                            {dim?.na ? 'n/a' : formatScore(dim?.score)}
                          </td>
                        );
                      })}
                      <td className="px-5 py-3 text-left">
                        <div className="flex flex-col gap-1">
                          <CopyableId label="meeting" value={row.qualityScoring.meeting_id} />
                          <CopyableId label="session" value={row.sessionId} />
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                          aria-expanded={isExpanded}
                          onClick={() => setExpandedRowKey(isExpanded ? null : rowKey)}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-customGrey transition-colors hover:bg-white/10 hover:text-white"
                        >
                          <ChevronDownIcon
                            className={mergeClassNames(
                              'h-4 w-4 transition-transform',
                              isExpanded ? 'rotate-180' : ''
                            )}
                          />
                        </button>
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr className="border-b border-white/5">
                        <td colSpan={9 + DIMENSION_KEYS.length} className="p-3">
                          <RowDetails row={row} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </TableShell>
        ) : (
          <EmptyState>No graded sessions for this month.</EmptyState>
        )}
      </section>

      {isModalOpen && (
        <DetailsModal
          {...activeModalView}
          mentorId={mentorId}
          studentId={studentId}
          sessionId={sessionId}
          enrollmentId={enrollmentId}
          isOpen={true}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default QualityScoresTab;
